import { useCallback } from 'react'

import * as anchor from '@coral-xyz/anchor'
import { Wallet } from '@coral-xyz/anchor'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { WalletContextState, useWallet } from '@solana/wallet-adapter-react'
import { useAnchorWallet } from '@solana/wallet-adapter-react'
import { ComputeBudgetProgram, Keypair, PublicKey, SendTransactionError, Transaction } from '@solana/web3.js'

import {
    MESSAGE_TRANSMITTER_PROGRAM_ID,
    TOKEN_MESSENGER_MINTER_PROGRAM_ID,
    connection,
    getHighPriorityConnection,
    initializeProgram,
} from '@src/chains/solana/anchor/setup'
import { SOLANA_USDC_ADDRESS, SOLANA_USDC_ADDRESS as SOLANA_USDC_PROGRAM_ID } from '@src/chains/solana/config'
import { findProgramAddress, getSolUSDCTokenAccount } from '@src/chains/solana/utils'
import { PriorityFeeLevel, getPriorityFee } from '@src/chains/solana/utils/priorityFee'
import { sendTransactionWithRetry } from '@src/chains/solana/utils/retryConnection'
import { solanaLogger as logger } from '@src/utils/logger'

type SolanaWallet = WalletContextState & {
    publicKey: PublicKey
    signTransaction(tx: Transaction): Promise<Transaction>
    signAllTransactions(txs: Transaction[]): Promise<Transaction[]>
}

const useDepositForBurn = () => {
    const { signTransaction } = useWallet()
    const wallet = useAnchorWallet()
    const solanaPublicKey = wallet?.publicKey

    const getProvider = useCallback(() => {
        return new anchor.AnchorProvider(connection, wallet as SolanaWallet, anchor.AnchorProvider.defaultOptions())
    }, [connection, wallet])

    const provider = getProvider()
    const program = initializeProgram(wallet as unknown as Wallet)

    const getDepositForBurnPdas = (destinationDomain: Number) => {
        const messageTransmitterAccount = findProgramAddress('message_transmitter', MESSAGE_TRANSMITTER_PROGRAM_ID)
        const tokenMessengerAccount = findProgramAddress('token_messenger', TOKEN_MESSENGER_MINTER_PROGRAM_ID)
        const tokenMinterAccount = findProgramAddress('token_minter', TOKEN_MESSENGER_MINTER_PROGRAM_ID)
        const localToken = findProgramAddress('local_token', TOKEN_MESSENGER_MINTER_PROGRAM_ID, [
            SOLANA_USDC_PROGRAM_ID,
        ])
        const remoteTokenMessengerKey = findProgramAddress(
            'remote_token_messenger',
            TOKEN_MESSENGER_MINTER_PROGRAM_ID,
            [destinationDomain.toString()]
        )
        const authorityPda = findProgramAddress('sender_authority', TOKEN_MESSENGER_MINTER_PROGRAM_ID)
        const eventAuthority = findProgramAddress('__event_authority', TOKEN_MESSENGER_MINTER_PROGRAM_ID)

        const pdas = {
            messageTransmitterAccount,
            tokenMessengerAccount,
            tokenMinterAccount,
            localToken,
            remoteTokenMessengerKey,
            authorityPda,
            eventAuthority,
        }
        return pdas
    }

    /**
     * Prepares the transaction data needed for depositForBurn, without executing it.
     * This function creates and partially signs the transaction (with the event account Keypair),
     * and returns a VersionedTransaction ready for the wallet to sign.
     */
    const getDepositForBurnData = useCallback(
        async (
            sendAmount: bigint,
            recipientAddress: string,
            destinationDomain: number,
            solanaWalletAddress: string,
            useDummyWallet: boolean = false, // dummy, used for gas fee simulation only.
            dummyWalletPk: PublicKey | null = null // dummy, used for gas fee simulation only.
        ) => {
            // If we are NOT using a dummy wallet, ensure we have a real wallet
            if (!useDummyWallet) {
                if (!wallet) {
                    throw new Error('Wallet is not connected')
                }
                if (!solanaPublicKey) {
                    throw new Error('Public Key is not provided')
                }
            }

            // Decide which public key to use as the "owner" (fee payer, etc.)
            const effectivePublicKey = useDummyWallet ? dummyWalletPk : solanaPublicKey

            if (!effectivePublicKey) {
                throw new Error(
                    'No effective public key available (neither a real wallet nor a dummy public key was provided).'
                )
            }

            // Check if the recipient address is an SNS domain and resolve it if needed
            let resolvedAddress = recipientAddress

            // If it's a Solana address in hex format (from EVM), convert it
            if (recipientAddress.startsWith('0x')) {
                const buffer = Buffer.from(recipientAddress.slice(2), 'hex')
                resolvedAddress = new PublicKey(buffer).toString()
            }

            // Create the PublicKey from the resolved address
            const mintRecipient = new PublicKey(resolvedAddress)

            const depositForBurnParams = {
                amount: new anchor.BN(sendAmount),
                destinationDomain,
                mintRecipient,
            }

            // If we are NOT using a dummy wallet, find the real USDC token account
            // If using a dummy wallet, we either skip this or set a dummy token account
            let burnTokenAccount: PublicKey | null = null

            if (!useDummyWallet) {
                burnTokenAccount = await getSolUSDCTokenAccount(effectivePublicKey)
                if (!burnTokenAccount) {
                    throw new Error('User USDC token account is not provided')
                }
            } else {
                // *** For simulation only: Hard-code or mock a token account if you need it. ***
                // *** Otherwise, you might skip references to 'burnTokenAccount' altogether. ***
                const pk = Keypair.generate().publicKey
                burnTokenAccount = pk
            }

            const pdas = getDepositForBurnPdas(destinationDomain)

            // Generate the event account Keypair for partial signing
            const messageSentEventAccountKeypair = Keypair.generate()

            const ownerPublicKey =
                (useDummyWallet && dummyWalletPk) || provider?.wallet?.publicKey || solanaWalletAddress

            const accounts = {
                owner: ownerPublicKey,
                eventRentPayer: ownerPublicKey,
                senderAuthorityPda: pdas.authorityPda.publicKey,
                burnTokenAccount: burnTokenAccount,
                messageTransmitter: pdas.messageTransmitterAccount.publicKey,
                tokenMessenger: pdas.tokenMessengerAccount.publicKey,
                remoteTokenMessenger: pdas.remoteTokenMessengerKey.publicKey,
                tokenMinter: pdas.tokenMinterAccount.publicKey,
                localToken: pdas.localToken.publicKey,
                burnTokenMint: SOLANA_USDC_ADDRESS,
                messageTransmitterProgram: MESSAGE_TRANSMITTER_PROGRAM_ID,
                tokenMessengerMinterProgram: TOKEN_MESSENGER_MINTER_PROGRAM_ID,
                messageSentEventData: messageSentEventAccountKeypair.publicKey,
                tokenProgram: TOKEN_PROGRAM_ID,
            }

            // Build the base transaction
            const unsignedTransaction = await program.methods
                .depositForBurn(depositForBurnParams)
                .accounts(accounts)
                .signers([messageSentEventAccountKeypair])
                .transaction()

            // Get a blockhash
            const { blockhash } = await connection.getLatestBlockhash('finalized')
            unsignedTransaction.recentBlockhash = blockhash

            // If you're using a dummy wallet, you might not have a real fee payer,
            // so just set the dummyPublicKey as feePayer (for simulation).
            unsignedTransaction.feePayer = effectivePublicKey

            // Add compute budget instructions
            const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
                units: 65000,
            })

            // Fetch the current priority fee (default to HIGH level) with the account address
            // This provides more accurate fee estimation for this specific transaction type
            const priorityFee = await getPriorityFee(PriorityFeeLevel.HIGH, effectivePublicKey.toString())

            const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
                microLamports: priorityFee,
            })

            const newTransaction = new Transaction().add(modifyComputeUnits).add(addPriorityFee)

            // Merge instructions
            unsignedTransaction.instructions.forEach(instruction => {
                newTransaction.add(instruction)
            })

            // Create a VersionedTransaction
            const transactionMessageV0 = new anchor.web3.TransactionMessage({
                payerKey: effectivePublicKey,
                recentBlockhash: blockhash,
                instructions: newTransaction.instructions,
            }).compileToV0Message()

            const versionedTransaction = new anchor.web3.VersionedTransaction(transactionMessageV0)

            // Partially sign with the event account (this is always offline-signed).
            versionedTransaction.sign([messageSentEventAccountKeypair])

            // Return the prepared transaction and the keypair
            // Including the keypair allows depositForBurn to create a fresh transaction with a new blockhash
            return {
                versionedTransaction,
                blockhash,
                messageSentEventAccountKeypair,
                instructions: newTransaction.instructions,
            }
        },

        [wallet, solanaPublicKey, provider, program]
    )

    /**
     * Executes the depositForBurn transaction:
     * - Constructs the prepared versionedTransaction using getDepositForBurnData
     * - Requests the wallet to sign it
     * - Sends the signed transaction to the network
     */
    const depositForBurn = useCallback(
        async (
            sendAmount: bigint,
            recipientAddress: string,
            destinationDomain: number,
            solanaWalletAddress: string,
            useHelius: boolean = false
        ) => {
            try {
                // Create a fresh connection for this specific transaction
                const highPriorityConnection = getHighPriorityConnection(false, useHelius)

                if (useHelius) {
                    logger.info('Using Helius RPC for transaction', { useHelius })
                }

                // Get a fresh blockhash FIRST - using 'finalized' for maximum reliability
                const { blockhash: freshBlockhash } = await highPriorityConnection.getLatestBlockhash('finalized')
                logger.info('Got fresh blockhash for transaction', { freshBlockhash })

                // Get transaction data including the messageSentEventAccountKeypair and instructions
                const { messageSentEventAccountKeypair, instructions } = await getDepositForBurnData(
                    sendAmount,
                    recipientAddress,
                    destinationDomain,
                    solanaWalletAddress
                )

                if (!solanaPublicKey) {
                    throw new Error('Solana public key is not available')
                }

                // Create a new transaction with the fresh blockhash
                const transaction = new Transaction()

                // Add all instructions to the transaction
                instructions.forEach(instruction => transaction.add(instruction))

                // Set the blockhash and fee payer
                transaction.recentBlockhash = freshBlockhash
                transaction.feePayer = solanaPublicKey

                // Partially sign with the event account keypair
                // This is required by Circle's CCTP protocol for tracking cross-chain messages
                transaction.partialSign(messageSentEventAccountKeypair)

                if (!signTransaction) {
                    throw new Error('Wallet does not support transaction signing')
                }

                // Sign with the connected wallet
                const signedTx = await signTransaction(transaction)

                // Send the signed transaction with retry mechanism
                const signature = await sendTransactionWithRetry(signedTx, {
                    skipPreflight: true,
                    maxRetries: 5,
                    useHelius,
                })

                return signature
            } catch (error) {
                if (error instanceof SendTransactionError) {
                    logger.error('Transaction simulation failed', {
                        error: error.message,
                        logs: error.logs,
                    })
                }
                throw error
            }
        },
        [signTransaction]
    )

    return { getDepositForBurnData, depositForBurn }
}

export default useDepositForBurn
